import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play, Pause, Square, X, Sparkles, User } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Mode: "characters" (OpenAI TTS via /api/narrator) or "device" (browser Web Speech API)
export const ReadAloud = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(() => localStorage.getItem("ra_mode") || "characters");

  // Character narrator state
  const [characters, setCharacters] = useState([]);
  const [character, setCharacter] = useState(() => localStorage.getItem("ra_character") || "whoracle");
  const [hd, setHd] = useState(() => localStorage.getItem("ra_hd") === "1");
  const [loadingChar, setLoadingChar] = useState(false);
  const audioRef = useRef(null);

  // Device (Web Speech API) state
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(() => localStorage.getItem("ra_voice") || "");
  const [rate, setRate] = useState(() => Number(localStorage.getItem("ra_rate") || 1));
  const [pitch, setPitch] = useState(() => Number(localStorage.getItem("ra_pitch") || 1));

  const [state, setState] = useState("idle"); // idle | speaking | paused

  // Load characters once
  useEffect(() => {
    fetch(`${BACKEND}/api/narrator/characters`)
      .then((r) => r.json())
      .then(setCharacters)
      .catch(() => setCharacters([]));
  }, []);

  // Load device voices
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const load = () => {
      const list = window.speechSynthesis.getVoices();
      setVoices(list);
      if (!voiceURI && list.length) {
        const preferred =
          list.find((v) => /en[-_]us/i.test(v.lang) && /female|zira|samantha|jenny/i.test(v.name)) ||
          list.find((v) => /^en/i.test(v.lang)) ||
          list[0];
        setVoiceURI(preferred.voiceURI);
      }
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist prefs
  useEffect(() => { localStorage.setItem("ra_mode", mode); }, [mode]);
  useEffect(() => { localStorage.setItem("ra_character", character); }, [character]);
  useEffect(() => { localStorage.setItem("ra_hd", hd ? "1" : "0"); }, [hd]);
  useEffect(() => { if (voiceURI) localStorage.setItem("ra_voice", voiceURI); }, [voiceURI]);
  useEffect(() => { localStorage.setItem("ra_rate", String(rate)); }, [rate]);
  useEffect(() => { localStorage.setItem("ra_pitch", String(pitch)); }, [pitch]);

  const grabPageText = () => {
    const scope =
      document.querySelector("main") ||
      document.querySelector("article") ||
      document.querySelector('[data-testid$="-page"]') ||
      document.body;
    if (!scope) return "";
    const clone = scope.cloneNode(true);
    clone.querySelectorAll("nav, header, footer, script, style, [data-read-skip]").forEach((n) => n.remove());
    return clone.innerText.replace(/\s+/g, " ").trim();
  };

  // ---------- Character narrator (OpenAI TTS) ----------
  const playCharacter = async () => {
    // stop anything else in flight
    stopAll();
    const text = grabPageText().slice(0, 3800);
    if (!text) return;
    setLoadingChar(true);
    try {
      const res = await fetch(`${BACKEND}/api/narrator/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, character, hd, speed: rate }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.playbackRate = rate;
      audio.onended = () => { setState("idle"); URL.revokeObjectURL(url); };
      audio.onpause = () => { if (!audio.ended) setState("paused"); };
      audio.onplay = () => setState("speaking");
      audioRef.current = audio;
      await audio.play();
    } catch (e) {
      console.error("narrator failed", e);
      setState("idle");
    } finally {
      setLoadingChar(false);
    }
  };

  const pauseCharacter = () => { audioRef.current?.pause(); };
  const resumeCharacter = () => { audioRef.current?.play(); };
  const stopCharacter = () => { const a = audioRef.current; if (a) { a.pause(); a.currentTime = 0; } setState("idle"); };

  // ---------- Device narrator (Web Speech) ----------
  const playDevice = () => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const text = grabPageText();
    if (!text) return;
    const u = new SpeechSynthesisUtterance(text.slice(0, 8000));
    const v = voices.find((x) => x.voiceURI === voiceURI);
    if (v) u.voice = v;
    u.rate = rate;
    u.pitch = pitch;
    u.onend = () => setState("idle");
    u.onerror = () => setState("idle");
    window.speechSynthesis.speak(u);
    setState("speaking");
  };
  const pauseDevice = () => { window.speechSynthesis.pause(); setState("paused"); };
  const resumeDevice = () => { window.speechSynthesis.resume(); setState("speaking"); };
  const stopDevice = () => { window.speechSynthesis.cancel(); setState("idle"); };

  // ---------- Unified controls ----------
  const stopAll = () => {
    stopCharacter();
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setState("idle");
  };
  const play = () => (mode === "characters" ? playCharacter() : playDevice());
  const pause = () => (mode === "characters" ? pauseCharacter() : pauseDevice());
  const resume = () => (mode === "characters" ? resumeCharacter() : resumeDevice());
  const stop = () => (mode === "characters" ? stopCharacter() : stopDevice());

  return (
    <>
      {/* Trigger button */}
      <button
        data-testid="read-aloud-toggle"
        onClick={() => setOpen((o) => !o)}
        title="read this page aloud"
        aria-label="Open read-aloud controls"
        className="fixed bottom-5 left-5 z-40 inline-flex items-center gap-2 bg-[color:var(--accent-amber)] text-black px-4 py-3 text-xs uppercase tracking-widest font-bold shadow-lg hover:bg-white duration-150"
        style={{ transitionProperty: "background-color" }}
      >
        {state === "speaking" ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        read aloud
      </button>

      {open && (
        <div
          data-testid="read-aloud-panel"
          data-read-skip
          className="fixed bottom-20 left-5 z-40 w-[22rem] max-h-[85vh] overflow-y-auto bg-[#0a0a0a] border border-[color:var(--accent-amber)] shadow-2xl p-5 text-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)]">// read aloud</div>
            <button
              data-testid="read-aloud-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-neutral-500 hover:text-white"
            ><X className="w-4 h-4" /></button>
          </div>

          {/* Mode switch */}
          <div className="grid grid-cols-2 gap-1 mb-4 border border-white/10 p-1">
            <button
              data-testid="ra-mode-characters"
              onClick={() => { stopAll(); setMode("characters"); }}
              className={`flex items-center justify-center gap-1 px-2 py-2 text-[10px] uppercase tracking-widest ${mode === "characters" ? "bg-[color:var(--accent-matrix)] text-black" : "text-neutral-400 hover:text-white"}`}
            ><Sparkles className="w-3 h-3" /> characters</button>
            <button
              data-testid="ra-mode-device"
              onClick={() => { stopAll(); setMode("device"); }}
              className={`flex items-center justify-center gap-1 px-2 py-2 text-[10px] uppercase tracking-widest ${mode === "device" ? "bg-[color:var(--accent-amber)] text-black" : "text-neutral-400 hover:text-white"}`}
            ><User className="w-3 h-3" /> device</button>
          </div>

          {/* Playback controls */}
          <div className="flex items-center gap-2 mb-4">
            {state !== "speaking" ? (
              <button
                data-testid="read-aloud-play"
                onClick={state === "paused" ? resume : play}
                disabled={loadingChar}
                className="inline-flex items-center gap-2 bg-[color:var(--accent-matrix)] text-black px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex-1 justify-center hover:opacity-90 disabled:opacity-50 duration-150"
              >
                <Play className="w-3 h-3" /> {loadingChar ? "summoning..." : (state === "paused" ? "resume" : "play")}
              </button>
            ) : (
              <button
                data-testid="read-aloud-pause"
                onClick={pause}
                className="inline-flex items-center gap-2 bg-[color:var(--accent-amber)] text-black px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex-1 justify-center hover:opacity-90 duration-150"
              ><Pause className="w-3 h-3" /> pause</button>
            )}
            <button
              data-testid="read-aloud-stop"
              onClick={stop}
              disabled={state === "idle"}
              className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 text-[10px] uppercase tracking-widest hover:border-white/50 duration-150 disabled:opacity-40"
            ><Square className="w-3 h-3" /> stop</button>
          </div>

          {mode === "characters" ? (
            <>
              <label className="block text-[10px] uppercase tracking-widest text-neutral-500 mb-2">character</label>
              <div className="space-y-1 mb-4">
                {characters.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`ra-character-${c.id}`}
                    onClick={() => setCharacter(c.id)}
                    className={`w-full text-left p-2 border ${character === c.id ? "border-[color:var(--accent-matrix)] bg-[color:var(--accent-matrix)]/10" : "border-white/10 hover:border-white/30"} duration-150`}
                    style={{ transitionProperty: "border-color, background-color" }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-white">{c.name}</span>
                      <span className="text-[9px] uppercase tracking-widest text-neutral-500">{c.openai_voice}</span>
                    </div>
                    <div className="text-[10px] text-neutral-500 mt-0.5 leading-snug">{c.blurb}</div>
                  </button>
                ))}
              </div>

              <label className="block text-[10px] uppercase tracking-widest text-neutral-500 mb-1">speed: {rate.toFixed(2)}x</label>
              <input
                data-testid="ra-char-rate"
                type="range" min="0.5" max="2" step="0.05" value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="w-full mb-3 accent-[color:var(--accent-matrix)]"
              />

              <label className="flex items-center gap-2 cursor-pointer mb-2" data-testid="ra-hd-toggle">
                <input type="checkbox" checked={hd} onChange={(e) => setHd(e.target.checked)} className="sr-only" />
                <span className={`w-9 h-4 border ${hd ? "border-[color:var(--accent-matrix)] bg-[color:var(--accent-matrix)]/30" : "border-white/20"} relative`}>
                  <span className={`absolute top-0.5 w-3 h-3 ${hd ? "left-4 bg-[color:var(--accent-matrix)]" : "left-0.5 bg-neutral-500"} duration-150`} style={{ transitionProperty: "left, background-color" }} />
                </span>
                <span className="text-xs text-neutral-300">HD voice <span className="text-neutral-600 text-[10px]">(slower, richer)</span></span>
              </label>

              <div className="mt-3 text-[10px] text-neutral-500 leading-relaxed">
                first ~3800 characters of this page. powered by OpenAI TTS.
              </div>
            </>
          ) : (
            <>
              <label className="block text-[10px] uppercase tracking-widest text-neutral-500 mb-1">voice ({voices.length} available)</label>
              {voices.length === 0 ? (
                <div
                  data-testid="read-aloud-no-voices"
                  className="w-full bg-black border border-white/15 text-neutral-500 px-2 py-2 mb-4 text-xs"
                >
                  no voices detected in this browser — install system voices in your OS settings, or try Chrome/Safari.
                </div>
              ) : (
                <select
                  data-testid="read-aloud-voice"
                  value={voiceURI}
                  onChange={(e) => setVoiceURI(e.target.value)}
                  className="w-full bg-black border border-white/15 text-white px-2 py-2 mb-4 text-xs"
                >
                  {voices.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>
                      {v.name} — {v.lang}{v.default ? " (default)" : ""}
                    </option>
                  ))}
                </select>
              )}

              <label className="block text-[10px] uppercase tracking-widest text-neutral-500 mb-1">speed: {rate.toFixed(2)}x</label>
              <input
                data-testid="read-aloud-rate"
                type="range" min="0.5" max="2" step="0.05" value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="w-full mb-3 accent-[color:var(--accent-amber)]"
              />
              <label className="block text-[10px] uppercase tracking-widest text-neutral-500 mb-1">pitch: {pitch.toFixed(2)}</label>
              <input
                data-testid="read-aloud-pitch"
                type="range" min="0.5" max="2" step="0.05" value={pitch}
                onChange={(e) => setPitch(Number(e.target.value))}
                className="w-full accent-[color:var(--accent-amber)]"
              />
            </>
          )}
        </div>
      )}
    </>
  );
};
